import {
    Directive,
    Input,
    ElementRef,
    Renderer2,
    AfterViewInit,
    OnDestroy,
    input,
    effect,
    inject,
} from '@angular/core';

/**
 * Adds an inner `<span>` with ellipsis styling and optional tooltip for overflowed text.
 *
 * The directive creates a `span.bh-ellipsis.bh-block` inside the host and updates it
 * when `text()` signal changes. When content overflows and no `tooltipElement` is provided,
 * the full text is set as the `title` attribute.
 *
 * Inputs:
 * - `text: string` — Displayed text (signal input).
 * - `tooltipElement?: ElementRef` — Custom tooltip element to remove when text fits.
 *
 * Example:
 * ```html
 * <div ellipsis [text]="user.fullName"></div>
 * ```
 */
@Directive({
    selector: '[ellipsis]',
    standalone: true,
})
export class EllipsisDirective implements AfterViewInit, OnDestroy {
    private hostElement: ElementRef = inject(ElementRef);

    private renderer = inject(Renderer2);

    /**
     * Custom tooltip element to be removed when text fits.
     */
    @Input() tooltipElement: ElementRef;

    /**
     * Text content to render inside the inner ellipsis container.
     */
    text = input<string>('');

    /**
     * Host native element reference.
     */
    nativeEl;

    /**
     * Inner span element that holds the ellipsis text.
     */
    ellipsisEl;

    private resizeObserver: ResizeObserver;

    constructor() {
        this.nativeEl = this.hostElement.nativeElement;
        effect(() => {
            this.createTextElement(this.text());

            setTimeout(() => {
                this.checkSize();
            });
        });
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.checkSize());
        this.resizeObserver.observe(this.nativeEl);
    }

    /**
     * Creates or updates the inner text element with necessary classes.
     */
    createTextElement(text: string) {
        if (this.ellipsisEl) {
            this.ellipsisEl.innerHTML = text;
            return;
        }

        this.ellipsisEl = this.renderer.createElement('span');
        const _text = this.renderer.createText(text);

        this.renderer.appendChild(this.ellipsisEl, _text);
        this.renderer.addClass(this.ellipsisEl, 'bh-ellipsis');
        this.renderer.addClass(this.ellipsisEl, 'bh-block');
        this.renderer.appendChild(this.nativeEl, this.ellipsisEl);
    }

    /**
     * Checks if text overflows and sets `title` or removes custom tooltip accordingly.
     */
    checkSize() {
        if (!this.ellipsisEl) return;

        if (this.ellipsisEl.offsetWidth < this.ellipsisEl.scrollWidth && !this.tooltipElement) {
            this.renderer.setAttribute(this.ellipsisEl, 'title', this.text());
        }
        if (this.ellipsisEl.offsetWidth >= this.ellipsisEl.scrollWidth && this.tooltipElement) {
            this.renderer.removeChild(this.nativeEl, this.tooltipElement);
        }
    }

    /**
     * Disconnects observers and cleans up created elements.
     */
    ngOnDestroy() {
        this.resizeObserver?.disconnect();
        if (this.ellipsisEl?.parentNode) {
            this.renderer.removeChild(this.nativeEl, this.ellipsisEl);
        }
        this.tooltipElement = null;
        this.nativeEl = null;
        this.ellipsisEl = null;
    }
}
